import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY = { quote: "", attribution: "", location: "", published: true, order: 0 };

function Row({ value, onChange, onSave, onDelete, saving }) {
  return (
    <div data-testid={`testimonial-row-${value.id || "new"}`} className="border border-mist p-6 space-y-4">
      <div>
        <label className="eyebrow text-ink/55">Quote</label>
        <textarea
          data-testid={`testimonial-quote-${value.id || "new"}`}
          value={value.quote}
          onChange={(e) => onChange({ ...value, quote: e.target.value })}
          rows={3}
          className="input-line mt-3 resize-y"
        />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="eyebrow text-ink/55">Attribution</label>
          <input data-testid={`testimonial-attribution-${value.id || "new"}`} type="text" value={value.attribution} onChange={(e) => onChange({ ...value, attribution: e.target.value })} className="input-line mt-3" />
        </div>
        <div>
          <label className="eyebrow text-ink/55">Location / date</label>
          <input data-testid={`testimonial-location-${value.id || "new"}`} type="text" value={value.location} onChange={(e) => onChange({ ...value, location: e.target.value })} className="input-line mt-3" />
        </div>
        <div>
          <label className="eyebrow text-ink/55">Order</label>
          <input data-testid={`testimonial-order-${value.id || "new"}`} type="number" value={value.order} onChange={(e) => onChange({ ...value, order: parseInt(e.target.value, 10) || 0 })} className="input-line mt-3" />
        </div>
      </div>
      <div className="flex items-center justify-between pt-2">
        <label className="flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-ink/65">
          <input type="checkbox" checked={value.published} onChange={(e) => onChange({ ...value, published: e.target.checked })} data-testid={`testimonial-published-${value.id || "new"}`} /> Published
        </label>
        <div className="flex items-center gap-3">
          {onDelete && (
            <button type="button" onClick={onDelete} className="text-[12px] tracking-[0.14em] uppercase text-ink/55 hover:text-copper inline-flex items-center gap-2" data-testid={`testimonial-delete-${value.id}`}>
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            data-testid={`testimonial-save-${value.id || "new"}`}
            className="btn-primary text-[12px] py-2 px-5 disabled:opacity-40"
          >
            {saving ? "Saving…" : value.id ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TestimonialsList() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ ...EMPTY });
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data));

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.quote.trim()) {
      toast.error("Quote is required");
      return;
    }
    setBusyId("new");
    try {
      await api.post("/admin/testimonials", draft);
      setDraft({ ...EMPTY });
      await load();
      toast.success("Testimonial added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    } finally {
      setBusyId(null);
    }
  };

  const update = async (item) => {
    setBusyId(item.id);
    try {
      const { id, created_at, updated_at, ...payload } = item;
      await api.put(`/admin/testimonials/${id}`, payload);
      await load();
      toast.success("Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      await load();
      toast.success("Deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <p className="eyebrow">Content</p>
      <h1 className="mt-3 font-serif text-4xl text-jade font-light">Testimonials</h1>
      <p className="mt-3 text-ink/65 text-[14px] font-light max-w-2xl">
        The first published testimonial (by order) appears on the homepage. Unpublished entries are hidden from the public site.
      </p>

      <div className="mt-12 space-y-6">
        {items.map((t) => (
          <Row
            key={t.id}
            value={t}
            onChange={(v) => setItems((arr) => arr.map((x) => (x.id === t.id ? v : x)))}
            onSave={() => update(items.find((x) => x.id === t.id))}
            onDelete={() => remove(t.id)}
            saving={busyId === t.id}
          />
        ))}
      </div>

      <div className="mt-12">
        <p className="eyebrow text-copper inline-flex items-center gap-2"><Plus size={14} /> Add a new testimonial</p>
        <div className="mt-5">
          <Row
            value={draft}
            onChange={setDraft}
            onSave={create}
            saving={busyId === "new"}
          />
        </div>
      </div>
    </div>
  );
}
